export function createFukushizaiBaseSyncService(deps) {
    const {
        entrydataRef,
        entrydetailRef,
        fnc,
        entryLoadingRef,
        fukushizaiBaseDirtyRef,
        fukushizaiDetailSyncingRef,
        fukushizaiProjectionGuardRef,
        fukushizaiRepresentativeRowRef,
        fukushizaiMasterNameCacheRef,
        fukushizaiMasterNameLoadingRef,
        selectedFukushizaiContextRef,
        pendingFukushizaiCopy,
        selectedSetItem,
        selectedGroup,
        FUKUSHIZAI_BASE_PANEL_TYPE,
        KOMONOFUDA_SIZE_RATE_FIELD_IDS,
        isEmpty,
        isEnabled,
        DicVal,
        normalizeFukushizaiContext,
        normalizeItemCd,
        normalizeGroupNo,
        normalizeColorCd,
        isSameFukushizaiItemGroupContext,
        extractMasterCodeFromDisplayValue,
        formatFukushizaiMasterDisplayValue,
        getDetailRowsByPanelType,
        getFukushizaiBaseFieldIds,
        getFukushizaiMasterFieldMeta,
        getFukushizaiEntryProjectionContext,
        pickFirstFukushizaiText,
        pickFukushizaiRepresentativeRow,
        readFukushizaiMasterCodeNameFromValue,
        readFukushizaiMasterNameFromStore,
        readFukushizaiContextFromRow,
        readRowValueIgnoreCase,
        registeredColorCodesForContext,
        fukushizaiColorCodesForContext,
        buildFukushizaiMasterValueUpdates,
        buildFukushizaiDebugGroupSnapshot,
        buildKomonoFudaLabelCacheKey,
        resolveFukushizaiBaseEditContext,
        resolveFukushizaiContext,
        resolveFukushizaiDisplayTargetColors,
        ensureDetailRowsByTypeForMutation,
        clearFukushizaiDisplayProjectionFields,
        primeFukushizaiMasterNameCache,
        primeFukushizaiMasterStoreFromRows,
        isFukushizaiBaseField,
        isFukushizaiBaseMasterField,
        applyFukushizaiDetailRowUpdates,
        finishFukushizaiDetailSync,
        createFukushizaiGroupDetailRow,
        requestKomonoFudaLabels,
        refreshKomonoFudaDisplayPanelOnly,
        clearKomonoFudaDisplayGridContents,
        readFukushizaiBaseDirtyState,
        clearFukushizaiBaseDirtyFields,
        logFukushizaiBaseSyncFlat,
        logFukushizaiMasterNameResolveFlat,
        debugFukushizaiSyncLog,
        rerender,
        refreshFukushizaiSectionViews
    } = deps;

    // entrydata 上の代表値から detail 行へ戻します。
    function syncFukushizaiDetailRowsFromEntryData(entryObj) {
        if (isEmpty(entryObj) || typeof entryObj !== "object") return false;
        const updates = {};
        getFukushizaiBaseFieldIds().forEach((fieldId) => {
            if (!Object.prototype.hasOwnProperty.call(entryObj, fieldId)) return;
            updates[fieldId] = entryObj[fieldId];
        });
        if (Object.keys(updates).length === 0) return false;
        const context = selectedFukushizaiContextRef.current || {};
        const matchInfo = {
            ...context,
            rowid: String(DicVal(fukushizaiRepresentativeRowRef.current, "rowid", "") ?? "")
        };
        const result = applyFukushizaiDetailRowUpdates(matchInfo, updates);
        return finishFukushizaiDetailSync(result);
    }

    // BASE master 値から code/name/source を正規化します。
    function normalizeFukushizaiBaseMasterCodeName(fieldId, value) {
        const entryObj = entrydataRef.current || {};
        const rawValue = value !== undefined ? value : DicVal(entryObj, fieldId, "");
        const valueCodeName = readFukushizaiMasterCodeNameFromValue(fieldId, rawValue);
        const code = valueCodeName.code;
        const meta = getFukushizaiMasterFieldMeta(fieldId, code);
        const cacheName = isEmpty(meta) ? "" : String(DicVal(fukushizaiMasterNameCacheRef.current, meta.cacheKey, "") ?? "").trim()
            || String(DicVal(fukushizaiMasterNameCacheRef.current, meta.noKeyCacheKey, "") ?? "").trim();
        let name = valueCodeName.name;
        let source = name === "" ? "" : "value";
        if (name === "") {
            name = readFukushizaiMasterNameFromStore(fieldId, code);
            if (name !== "") source = "masterStore";
        }
        if (name === "") source = "";
        debugFukushizaiSyncLog("[Kakousiji][fukushizai master name resolve]", {
            fieldId,
            valueType: isEmpty(rawValue) ? "empty" : typeof rawValue,
            value: typeof rawValue === "object" ? readFukushizaiMasterCodeNameFromValue(fieldId, rawValue) : String(rawValue ?? ""),
            newCode: code,
            newName: name,
            source,
            dataid: DicVal(meta, "dataid", ""),
            dbcolumn: DicVal(meta, "dbcolumn", ""),
            keyinfo: DicVal(meta, "keyinfo", ""),
            hasMasterStoreName: source === "masterStore",
            hasCacheName: cacheName !== "",
            entryValueCode: extractMasterCodeFromDisplayValue(DicVal(entryObj, fieldId, "")),
            detailRowCount: getDetailRowsByPanelType(entrydetailRef.current, FUKUSHIZAI_BASE_PANEL_TYPE).length
        });
        return { code, name, source };
    }

    // 空でない BASE 更新が含まれるかを判定します。
    function hasNonEmptyFukushizaiBaseUpdate(updates) {
        return Object.keys(updates || {}).some((key) => {
            if (isEmpty(key)) return false;
            const value = updates[key];
            if (isEmpty(value)) return false;
            if (typeof value === "object") {
                const codeName = readFukushizaiMasterCodeNameFromValue(key, value);
                return codeName.code !== "" || codeName.name !== "";
            }
            return String(value ?? "").trim() !== "";
        });
    }

    // item/group から遅延生成用の color を解決します。
    function resolveFukushizaiLazyDetailColor(context) {
        const normalized = normalizeFukushizaiContext(context);
        if (normalized.colorCd !== "") return normalized.colorCd;
        const registeredColors = registeredColorCodesForContext(normalized.itemCd, normalized.groupNo);
        if (registeredColors.length !== 0) return registeredColors[0];
        const knownColors = fukushizaiColorCodesForContext(normalized.itemCd, normalized.groupNo);
        return knownColors[0] || "";
    }

    // BASE 更新時に必要なら detail 行を遅延生成します。
    function createFukushizaiLazyDetailRowForBaseUpdate(detailRows, context, updates) {
        const normalized = normalizeFukushizaiContext(context);
        if (normalized.itemCd === "" || normalized.groupNo === "") return null;
        if (!hasNonEmptyFukushizaiBaseUpdate(updates)) return null;
        const colorCd = resolveFukushizaiLazyDetailColor(normalized);
        if (colorCd === "") return null;
        const row = createFukushizaiGroupDetailRow(normalized.itemCd, normalized.groupNo, colorCd, null, false);
        (Array.isArray(detailRows) ? detailRows : []).push(row);
        return { row, index: Array.isArray(detailRows) ? detailRows.length - 1 : -1 };
    }

    // 現在 context の D_SSDZ 行へ BASE 更新値を反映します。
    function applyFukushizaiBaseUpdatesToCurrentContext(updates, contextOverride = null) {
        const detail = ensureDetailRowsByTypeForMutation(FUKUSHIZAI_BASE_PANEL_TYPE);
        const detailRows = detail.rows;
        const context = resolveFukushizaiContext(detailRows, contextOverride || selectedFukushizaiContextRef.current);
        if (context.itemCd === "" || context.groupNo === "") {
            debugFukushizaiSyncLog("[Kakousiji][fukushizai base sync skipped: empty context]", {
                context,
                updates
            });
            return { changed: false, index: -1, row: null, context, updatedCount: 0 };
        }

        const representative = pickFukushizaiRepresentativeRow(detailRows, context, false);
        const targetIndexes = [];
        detailRows.forEach((row, index) => {
            if (isEmpty(row)) return;
            const itemCd = String(readRowValueIgnoreCase(row, "SSDZ_ITMCITEMCD") ?? "").trim();
            const groupNo = normalizeGroupNo(readRowValueIgnoreCase(row, "SSDZ_FUKUSIZAICOLORGRP"));
            if (context.itemCd !== "" && itemCd !== context.itemCd) return;
            if (context.groupNo !== "" && groupNo !== context.groupNo) return;
            targetIndexes.push(index);
        });
        if (targetIndexes.length === 0) {
            const created = createFukushizaiLazyDetailRowForBaseUpdate(detailRows, context, updates);
            if (isEmpty(created) || created.index < 0) return { changed: false, index: -1, row: null, context, updatedCount: 0 };
            targetIndexes.push(created.index);
        }
        if (!isEmpty(representative?.row) && representative.index >= 0 && targetIndexes.includes(representative.index)) {
            targetIndexes.splice(targetIndexes.indexOf(representative.index), 1);
            targetIndexes.unshift(representative.index);
        }

        let changed = false;
        let firstIndex = -1;
        let firstRow = null;
        let updatedCount = 0;
        targetIndexes.forEach((index) => {
            const currentRow = detailRows[index];
            if (isEmpty(currentRow)) return;
            const nextRow = { ...currentRow };
            let rowChanged = false;
            Object.keys(updates || {}).forEach((key) => {
                if (isEmpty(key)) return;
                if (nextRow[key] === updates[key]) return;
                nextRow[key] = updates[key];
                rowChanged = true;
            });
            if (!rowChanged) return;
            clearFukushizaiDisplayProjectionFields(nextRow);
            detailRows[index] = nextRow;
            changed = true;
            updatedCount++;
            if (firstIndex === -1) {
                firstIndex = index;
                firstRow = nextRow;
            }
        });
        if (firstIndex === -1 && !isEmpty(representative?.row)) {
            firstIndex = representative.index;
            firstRow = representative.row;
        }
        if (firstIndex >= 0 && !isEmpty(firstRow)) {
            fukushizaiRepresentativeRowRef.current = {
                rowid: String(DicVal(firstRow, "rowid", firstIndex) ?? firstIndex),
                index: firstIndex
            };
        }
        return {
            changed,
            index: firstIndex,
            row: firstRow,
            context: isEmpty(firstRow) ? context : readFukushizaiContextFromRow(firstRow),
            updatedCount
        };
    }

    // dirty fields を D_SSDZ detail へ同期します。
    function syncFukushizaiBaseDirtyFieldsToDetail(options = {}) {
        const context = normalizeFukushizaiContext(DicVal(options, "context", selectedFukushizaiContextRef.current));
        const timing = String(DicVal(options, "timing", "base-immediate") ?? "base-immediate").trim() || "base-immediate";
        const reason = String(DicVal(options, "reason", "") ?? "").trim();
        const dirtyState = readFukushizaiBaseDirtyState(context);
        const dirtyFields = dirtyState.fields || {};
        const dirtyFieldIds = Object.keys(dirtyFields);
        const targetColors = context.itemCd !== "" && context.groupNo !== "" ? resolveFukushizaiDisplayTargetColors(context) : [];
        const debugContext = {
            oldContext: normalizeFukushizaiContext(DicVal(fukushizaiBaseDirtyRef.current || {}, "context", null)),
            currentContext: normalizeFukushizaiContext(selectedFukushizaiContextRef.current),
            selectedItem: normalizeItemCd(selectedSetItem),
            selectedGroup: normalizeGroupNo(selectedGroup),
            selectedColor: normalizeColorCd(selectedFukushizaiContextRef.current?.colorCd),
            targetItem: context.itemCd,
            targetGroup: context.groupNo,
            projectionSyncing: isEnabled(fukushizaiDetailSyncingRef.current) || isEnabled(fukushizaiProjectionGuardRef.current),
            dirty: dirtyFieldIds.length !== 0
        };

        // item/group がない場合は対象 detail 行を決められないため同期しません。
        if (context.itemCd === "" || context.groupNo === "") {
            logFukushizaiBaseSyncFlat({ timing, reason, allowed: false, context, ...debugContext, dirtyFields: dirtyFieldIds, updates: {}, targetColors, updatedColumns: [], updatedCount: 0, skipReason: "missing-item-group" });
            return { changed: false, updatedCount: 0, context, skipReason: "missing-item-group" };
        }
        if (dirtyFieldIds.length === 0) {
            logFukushizaiBaseSyncFlat({ timing, reason, allowed: false, context, ...debugContext, dirtyFields: [], updates: {}, targetColors, updatedColumns: [], updatedCount: 0, skipReason: "no-dirty-fields" });
            return { changed: false, updatedCount: 0, context, skipReason: "no-dirty-fields" };
        }
        if (targetColors.length === 0) {
            logFukushizaiBaseSyncFlat({ timing, reason, allowed: false, context, ...debugContext, dirtyFields: dirtyFieldIds, updates: dirtyFields, targetColors, updatedColumns: dirtyFieldIds, updatedCount: 0, skipReason: "missing-target-colors" });
            return { changed: false, updatedCount: 0, context, skipReason: "missing-target-colors" };
        }

        const result = applyFukushizaiBaseUpdatesToCurrentContext(dirtyFields, context);
        const updatedCount = Number(DicVal(result, "updatedCount", 0)) || 0;
        if (updatedCount === 0 && !isEnabled(DicVal(result, "changed", false))) {
            logFukushizaiBaseSyncFlat({
                timing,
                reason,
                allowed: false,
                context,
                ...debugContext,
                dirtyFields: dirtyFieldIds,
                updates: dirtyFields,
                targetColors,
                updatedColumns: dirtyFieldIds,
                updatedCount: 0,
                skipReason: targetColors.length === 0 ? "no-target-colors" : "no-target-rows"
            });
            clearFukushizaiBaseDirtyFields(context);
            return { ...result, changed: false, updatedCount: 0, context, skipReason: targetColors.length === 0 ? "no-target-colors" : "no-target-rows" };
        }
        logFukushizaiBaseSyncFlat({
            timing,
            reason,
            allowed: true,
            context,
            ...debugContext,
            dirtyFields: dirtyFieldIds,
            updates: dirtyFields,
            targetColors,
            updatedColumns: dirtyFieldIds,
            updatedCount,
            skipReason: ""
        });
        clearFukushizaiBaseDirtyFields(context);
        return result;
    }

    // 非同期取得した master 名を current context へ反映します。
    function applyResolvedFukushizaiMasterName(fieldId, code, name, contextSnapshot) {
        const nextCode = String(code ?? "").trim();
        const nextName = String(name ?? "").trim();
        if (nextCode === "" || nextName === "") return false;
        const detailRows = getDetailRowsByPanelType(entrydetailRef.current, FUKUSHIZAI_BASE_PANEL_TYPE);
        if (!Array.isArray(detailRows) || detailRows.length === 0) return false;
        const context = resolveFukushizaiContext(detailRows, contextSnapshot);

        // context が変わっていたら古い非同期応答なので反映しません。
        if (!isSameFukushizaiItemGroupContext(selectedFukushizaiContextRef.current, context)) {
            debugFukushizaiSyncLog("[Kakousiji][fukushizai master name async skipped: stale context]", {
                fieldId,
                code: nextCode,
                name: nextName,
                context,
                selectedContext: selectedFukushizaiContextRef.current
            });
            return false;
        }

        const picked = pickFukushizaiRepresentativeRow(detailRows, context, false);
        const currentCode = extractMasterCodeFromDisplayValue(DicVal(DicVal(picked, "row", null), fieldId, ""));
        if (currentCode !== nextCode) {
            debugFukushizaiSyncLog("[Kakousiji][fukushizai master name async skipped: stale code]", {
                fieldId,
                responseCode: nextCode,
                currentCode,
                context
            });
            return false;
        }

        primeFukushizaiMasterNameCache(fieldId, nextCode, nextName);
        logFukushizaiMasterNameResolveFlat({
            timing: "on-demand-resolved",
            panelType: "",
            rowIndex: -1,
            sourceColumnId: fieldId,
            displayField: "",
            code: nextCode,
            resolvedName: nextName,
            displayValue: formatFukushizaiMasterDisplayValue(nextCode, nextName),
            cacheKey: String(DicVal(getFukushizaiMasterFieldMeta(fieldId, nextCode), "cacheKey", "") ?? ""),
            source: "on-demand",
            selectedItem: selectedSetItem,
            selectedGroup,
            currentItem: context.itemCd,
            currentGroup: context.groupNo,
            applied: true,
            skipReason: ""
        });
        debugFukushizaiSyncLog("[Kakousiji][fukushizai master name async applied]", {
            fieldId,
            code: nextCode,
            name: nextName,
            context,
            updatedCount: 0
        });
        refreshFukushizaiSectionViews(context.itemCd, context.groupNo, pendingFukushizaiCopy, context);
        rerender();
        if (!isEmpty(fnc.current) && typeof fnc.current.modify === "function") fnc.current.modify(true);
        return true;
    }

    // master 名が足りない場合に on-demand 取得します。
    function requestFukushizaiMasterName(fieldId, code, contextSnapshot) {
        const targetCode = String(code ?? "").trim();
        if (targetCode === "") return false;
        const meta = getFukushizaiMasterFieldMeta(fieldId, targetCode);
        if (isEmpty(meta)) return false;
        const loadingKey = meta.cacheKey;
        if (isEnabled(DicVal(fukushizaiMasterNameLoadingRef.current, loadingKey, false))) return false;
        if (isEmpty(fnc.current) || typeof fnc.current.getMasterdata !== "function") return false;
        fukushizaiMasterNameLoadingRef.current[loadingKey] = true;
        const finish = function () {
            fukushizaiMasterNameLoadingRef.current[loadingKey] = false;
        };
        const others = [{ columnid: fieldId, dbcolumn: meta.dbcolumn }];
        try {
            const requestContext = normalizeFukushizaiContext(contextSnapshot);
            logFukushizaiMasterNameResolveFlat({
                timing: "on-demand-start",
                panelType: "",
                rowIndex: -1,
                sourceColumnId: fieldId,
                displayField: "",
                code: targetCode,
                resolvedName: "",
                displayValue: targetCode,
                cacheKey: meta.cacheKey,
                source: "on-demand-start",
                selectedItem: selectedSetItem,
                selectedGroup,
                currentItem: requestContext.itemCd,
                currentGroup: requestContext.groupNo,
                applied: false,
                skipReason: ""
            });
            fnc.current.getMasterdata(meta.dataid, targetCode, meta.keyinfo, others, null, (data) => {
                try {
                    if (!isEmpty(data) && typeof fnc.current?.setMaster === "function") {
                        const masterRow = { ...data, dataid: meta.dataid, code: targetCode };
                        if (meta.keyinfo !== "") masterRow.keyinfo = meta.keyinfo;
                        fnc.current.setMaster(masterRow);
                    }
                    const name = pickFirstFukushizaiText(data, [
                        meta.dbcolumn,
                        `${fieldId}_nm`,
                        `${fieldId}_NM`,
                        "name",
                        "label"
                    ]);
                    if (name !== "") {
                        applyResolvedFukushizaiMasterName(fieldId, targetCode, name, contextSnapshot);
                    }
                    debugFukushizaiSyncLog("[Kakousiji][fukushizai master name fetched]", {
                        fieldId,
                        code: targetCode,
                        name,
                        dataid: meta.dataid,
                        dbcolumn: meta.dbcolumn,
                        keyinfo: meta.keyinfo,
                        found: name !== ""
                    });
                } finally {
                    finish();
                }
            }, false);
            return true;
        } catch (error) {
            finish();
            debugFukushizaiSyncLog("[Kakousiji][fukushizai master name fetch failed]", {
                fieldId,
                code: targetCode,
                dataid: meta.dataid,
                dbcolumn: meta.dbcolumn,
                keyinfo: meta.keyinfo,
                error
            });
            return false;
        }
    }

    // 副資材BASE項目 1 件の変更を detail と表示へ反映します。
    function syncFukushizaiBaseFieldValueToDetail(fieldId, value) {
        if (isEmpty(entrydataRef.current)) return false;
        const updates = {};
        const currentDetailRows = getDetailRowsByPanelType(entrydetailRef.current, FUKUSHIZAI_BASE_PANEL_TYPE);
        const contextSnapshot = resolveFukushizaiBaseEditContext();
        const currentRepresentative = pickFukushizaiRepresentativeRow(currentDetailRows, contextSnapshot, false);
        const previousDetailCode = extractMasterCodeFromDisplayValue(DicVal(DicVal(currentRepresentative, "row", null), fieldId, ""));
        const previousEntryCode = extractMasterCodeFromDisplayValue(DicVal(entrydataRef.current, fieldId, ""));
        const previousCode = previousDetailCode || previousEntryCode;

        // context が未確定だと対象 item/group を特定できないため同期しません。
        if (contextSnapshot.itemCd === "" || contextSnapshot.groupNo === "") {
            debugFukushizaiSyncLog("[Kakousiji][fukushizai base sync skipped: missing projected context]", {
                fieldId,
                selectedContext: selectedFukushizaiContextRef.current,
                projectedContext: getFukushizaiEntryProjectionContext(entrydataRef.current)
            });
            return false;
        }

        if (isFukushizaiBaseMasterField(fieldId)) {
            const codeName = normalizeFukushizaiBaseMasterCodeName(fieldId, value);
            Object.assign(updates, buildFukushizaiMasterValueUpdates(fieldId, codeName.code));
            if (codeName.code !== "" && codeName.name !== "") {
                primeFukushizaiMasterNameCache(fieldId, codeName.code, codeName.name);
            }
            if (codeName.code !== "" && codeName.name === "") {
                requestFukushizaiMasterName(fieldId, codeName.code, contextSnapshot);
            }
            debugFukushizaiSyncLog("[Kakousiji][fukushizai base master sync]", {
                fieldId,
                oldCode: previousCode,
                code: codeName.code,
                name: codeName.name,
                source: codeName.source
            });
        } else {
            updates[fieldId] = value;
        }

        // 小物札コード変更時は関連するサイズ率表示をクリアします。
        if (fieldId === "SSDZ_KOMONOFUDACD") {
            const nextCode = String(DicVal(updates, fieldId, "") ?? "").trim();
            const shouldClearKomonoContents = !entryLoadingRef.current && previousCode !== "" && previousCode !== nextCode;
            if (shouldClearKomonoContents) {
                KOMONOFUDA_SIZE_RATE_FIELD_IDS.forEach((sourceColumnId) => {
                    updates[sourceColumnId] = "";
                });
                clearKomonoFudaDisplayGridContents();
            }
        }

        fukushizaiBaseDirtyRef.current = { context: contextSnapshot, fields: { ...updates } };
        const result = syncFukushizaiBaseDirtyFieldsToDetail({
            context: contextSnapshot,
            timing: "base-immediate",
            reason: fieldId
        });
        if (fieldId === "SSDZ_KOMONOFUDACD") {
            const newCode = String(DicVal(updates, fieldId, "") ?? "").trim();
            const updatedRows = Number(DicVal(result, "updatedCount", 0)) || 0;
            if (previousCode !== newCode || updatedRows !== 0) {
                debugFukushizaiSyncLog("[Kakousiji][komonofuda change]", {
                    selectedContext: selectedFukushizaiContextRef.current,
                    normalizedContext: contextSnapshot,
                    oldCode: previousCode,
                    newCode,
                    updatedRows,
                    clearedSizeritu: !entryLoadingRef.current && previousCode !== "" && previousCode !== newCode,
                    group01CodesAfter: buildFukushizaiDebugGroupSnapshot(contextSnapshot.itemCd, "01").map((row) => DicVal(row, "SSDZ_KOMONOFUDACD", "")),
                    group02CodesAfter: buildFukushizaiDebugGroupSnapshot(contextSnapshot.itemCd, "02").map((row) => DicVal(row, "SSDZ_KOMONOFUDACD", "")),
                    entrydataCodeAfter: String(DicVal(entrydataRef.current, "SSDZ_KOMONOFUDACD", "") ?? "").trim(),
                    requestKey: buildKomonoFudaLabelCacheKey(newCode)
                });
            }
        }

        debugFukushizaiSyncLog("[Kakousiji][fukushizai base detail updated]", {
            fieldId,
            updatedCount: DicVal(result, "updatedCount", 0),
            context: DicVal(result, "context", null)
        });
        const synced = finishFukushizaiDetailSync(result, pendingFukushizaiCopy, {
            refresh: false,
            flushBeforeRefresh: false,
            rerender: false,
            updateSelection: false
        });
        if (synced) {
            const resultContext = normalizeFukushizaiContext(DicVal(result, "context", contextSnapshot));
            refreshFukushizaiSectionViews(resultContext.itemCd, resultContext.groupNo, pendingFukushizaiCopy, resultContext);
            if (!isEmpty(fnc.current) && typeof fnc.current.modify === "function") fnc.current.modify(true);
        }
        if (fieldId === "SSDZ_KOMONOFUDACD") {
            const code = String(DicVal(updates, fieldId, "") ?? "").trim();
            const name = readFukushizaiMasterNameFromStore(fieldId, code);
            if (code !== "") requestKomonoFudaLabels(code, name);
            refreshKomonoFudaDisplayPanelOnly();
        }
        if (isFukushizaiBaseMasterField(fieldId)) {
            primeFukushizaiMasterStoreFromRows(getDetailRowsByPanelType(entrydetailRef.current, FUKUSHIZAI_BASE_PANEL_TYPE));
        }
        return synced;
    }

    return {
        syncFukushizaiDetailRowsFromEntryData,
        normalizeFukushizaiBaseMasterCodeName,
        hasNonEmptyFukushizaiBaseUpdate,
        resolveFukushizaiLazyDetailColor,
        createFukushizaiLazyDetailRowForBaseUpdate,
        applyFukushizaiBaseUpdatesToCurrentContext,
        syncFukushizaiBaseDirtyFieldsToDetail,
        applyResolvedFukushizaiMasterName,
        requestFukushizaiMasterName,
        syncFukushizaiBaseFieldValueToDetail
    };
}
